import api from './api';
import securityService from './securityService';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
export const authService = {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - User data and token
   */
  async login(email, password) {
    const encryptedPassword = await securityService.encrypt(password);
    const response = await api.post('/auth/login', { email, password: encryptedPassword });
    
    if (response.token) {
      localStorage.setItem('authToken', response.token);
    }

    // Store userId from response
    if (response.userId) {
      localStorage.setItem('userId', response.userId);
    }
    
    // Store user role (optional, useful for role-based routing)
    if (response.role) {
      localStorage.setItem('userRole', response.role);
    }

    // Store user fullName 
    if (response.fullName) {
      localStorage.setItem('fullName', response.fullName);
    }
    
    if (response.companySize) {
      localStorage.setItem('companySize', response.companySize);
    }
    
    return response;
  },

  /**
   * Login user
   * @param {Object} userData - User Login data
   * @returns {Promise<Object>} - User data and token
   */
  async loginUser(userData) {
    try{
      const encryptedPassword = await securityService.encrypt(userData.password);
      const payload = { ...userData, password: encryptedPassword };
      const response = await api.post('/auth/login', payload);

      if (response.requireMfa) {
        localStorage.setItem('mfaPendingEmail', response.email);
        localStorage.setItem('mfaPendingType', 'LOGIN');
        if (response.orgAccessRequired !== undefined) {
          localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
        }
        if (response.orgId) {
          localStorage.setItem('orgId', response.orgId);
        }
        if (response.orgName) {
          localStorage.setItem('orgName', response.orgName);
        }
        if (response.membershipStatus) {
          localStorage.setItem('membershipStatus', response.membershipStatus);
        }
        if (response.orgOwnerEmail) {
          localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
        }
        return { requireMfa: true, email: response.email };
      }
      
      if (response.token) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('isAuthenticated', 'true');
      }
      if (response.userId) {
        localStorage.setItem('userId', response.userId);
      }
      if (response.email) {
        localStorage.setItem('userEmail', response.email);
      }
      if (response.role) {
        localStorage.setItem('userRole', response.role);
      }
      if (response.fullName) {
        localStorage.setItem('fullName', response.fullName);
      }
      if (response.onboardingCompleted) {
        localStorage.setItem('onboardingComplete', response.onboardingCompleted);
      }
      if (response.plan) {
        localStorage.setItem('plan', response.plan);
      }
    if (response.orgAccessRequired !== undefined) {
      localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
    }
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
    }
    if (response.orgName) {
      localStorage.setItem('orgName', response.orgName);
    }
    if (response.membershipStatus) {
      localStorage.setItem('membershipStatus', response.membershipStatus);
    }
    if (response.orgOwnerEmail) {
      localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
    }
    if (response.subscriptionStatus) {
      localStorage.setItem('subscriptionStatus', response.subscriptionStatus);
      localStorage.removeItem('trialSubscriptionSkipped');
    }
    if (response.companySize) {
      localStorage.setItem('companySize', response.companySize);
    }
    if (response.trialStart) {
        localStorage.setItem('trialStart', response.trialStart);
      }
    if (response.trialEnd) {
        localStorage.setItem('trialEnd', response.trialEnd);
      }
      if (response.daysRemaining !== undefined) {
        localStorage.setItem('daysRemaining', String(response.daysRemaining));
      }
      if (response.aiQuotaRemaining !== undefined) {
        localStorage.setItem('aiQuotaRemaining', String(response.aiQuotaRemaining));
      }
      if (response.aiQuotaTotal !== undefined) {
        localStorage.setItem('aiQuotaTotal', String(response.aiQuotaTotal));
      }
      if (response.isOrgAdmin !== undefined) {
        localStorage.setItem('isOrgAdmin', String(response.isOrgAdmin));
      }
      if (response.isOrgOwner !== undefined) {
        localStorage.setItem('isOrgOwner', String(response.isOrgOwner));
      }
      if (response.subscriptionRequired !== undefined) {
        localStorage.setItem('subscriptionRequired', String(response.subscriptionRequired));
      }
      
      return response;
    }
    catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  async loginWithMfa(email, code, type = 'LOGIN') {
    const response = await api.post('/auth/login/mfa', { email, code, type });
    if (response.token) {
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('isAuthenticated', 'true');
    }
    if (response.userId) {
      localStorage.setItem('userId', response.userId);
    }
    if (response.email) {
      localStorage.setItem('userEmail', response.email);
    }
    if (response.role) {
      localStorage.setItem('userRole', response.role);
    }
    if (response.fullName) {
      localStorage.setItem('fullName', response.fullName);
    }
    if (response.onboardingCompleted) {
      localStorage.setItem('onboardingComplete', response.onboardingCompleted);
    }
    if (response.plan) {
      localStorage.setItem('plan', response.plan);
    }
    if (response.orgAccessRequired !== undefined) {
      localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
    }
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
    }
    if (response.orgName) {
      localStorage.setItem('orgName', response.orgName);
    }
    if (response.membershipStatus) {
      localStorage.setItem('membershipStatus', response.membershipStatus);
    }
    if (response.orgOwnerEmail) {
      localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
    }
    if (response.subscriptionStatus) {
      localStorage.setItem('subscriptionStatus', response.subscriptionStatus);
      localStorage.removeItem('trialSubscriptionSkipped');
    }
    if (response.trialStart) {
      localStorage.setItem('trialStart', response.trialStart);
    }
    if (response.trialEnd) {
      localStorage.setItem('trialEnd', response.trialEnd);
    }
    if (response.daysRemaining !== undefined) {
      localStorage.setItem('daysRemaining', String(response.daysRemaining));
    }
    if (response.aiQuotaRemaining !== undefined) {
      localStorage.setItem('aiQuotaRemaining', String(response.aiQuotaRemaining));
    }
    if (response.aiQuotaTotal !== undefined) {
      localStorage.setItem('aiQuotaTotal', String(response.aiQuotaTotal));
    }
    if (response.isOrgAdmin !== undefined) {
      localStorage.setItem('isOrgAdmin', String(response.isOrgAdmin));
    }
    if (response.isOrgOwner !== undefined) {
      localStorage.setItem('isOrgOwner', String(response.isOrgOwner));
    }
    if (response.subscriptionRequired !== undefined) {
      localStorage.setItem('subscriptionRequired', String(response.subscriptionRequired));
    }
    localStorage.removeItem('mfaPendingEmail');
    localStorage.removeItem('mfaPendingType');
    return response;
  },

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - User data and token
   */
  async register(userData) {
    const encryptedPassword = await securityService.encrypt(userData.password);
    const payload = { ...userData, password: encryptedPassword };
    const response = await api.post('/auth/register', payload);

    if (response.requireMfa) {
      localStorage.setItem('mfaPendingEmail', response.email);
      localStorage.setItem('mfaPendingType', 'SIGNUP');
      if (response.plan) {
        localStorage.setItem('plan', response.plan);
      }
      if (response.orgAccessRequired !== undefined) {
        localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
      }
      if (response.orgId) {
        localStorage.setItem('orgId', response.orgId);
      }
      if (response.orgName) {
        localStorage.setItem('orgName', response.orgName);
      }
      if (response.membershipStatus) {
        localStorage.setItem('membershipStatus', response.membershipStatus);
      }
      return { requireMfa: true, email: response.email, userId: response.userId, role: response.role, fullName: response.fullName, plan: response.plan };
    }
    
    if (response.token) {
      localStorage.setItem('authToken', response.token);
    }
    if (response.userId) {
      localStorage.setItem('userId', response.userId);
    }
    if (response.email) {
      localStorage.setItem('userEmail', response.email);
    }
    if (response.role) {
      localStorage.setItem('userRole', response.role);
    }
    if (response.fullName) {
      localStorage.setItem('fullName', response.fullName);
    }
    if (response.plan) {
      localStorage.setItem('plan', response.plan);
    }
    if (response.orgAccessRequired !== undefined) {
      localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
    }
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
    }
    if (response.orgName) {
      localStorage.setItem('orgName', response.orgName);
    }
    if (response.membershipStatus) {
      localStorage.setItem('membershipStatus', response.membershipStatus);
    }
    if (response.orgOwnerEmail) {
      localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
    }
    if (response.subscriptionStatus) {
      localStorage.setItem('subscriptionStatus', response.subscriptionStatus);
      localStorage.removeItem('trialSubscriptionSkipped');
    }
    if (response.trialStart) {
      localStorage.setItem('trialStart', response.trialStart);
    }
    if (response.trialEnd) {
      localStorage.setItem('trialEnd', response.trialEnd);
    }
    if (response.daysRemaining !== undefined) {
      localStorage.setItem('daysRemaining', String(response.daysRemaining));
    }
    if (response.aiQuotaRemaining !== undefined) {
      localStorage.setItem('aiQuotaRemaining', String(response.aiQuotaRemaining));
    }
    if (response.aiQuotaTotal !== undefined) {
      localStorage.setItem('aiQuotaTotal', String(response.aiQuotaTotal));
    }
    if (response.isOrgAdmin !== undefined) {
      localStorage.setItem('isOrgAdmin', String(response.isOrgAdmin));
    }
    if (response.isOrgOwner !== undefined) {
      localStorage.setItem('isOrgOwner', String(response.isOrgOwner));
    }
    if (response.subscriptionRequired !== undefined) {
      localStorage.setItem('subscriptionRequired', String(response.subscriptionRequired));
    }
    
    return response;
  },
  
  /**
   * Logout user
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userData');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('fullName');
      localStorage.removeItem('orgAccessRequired');
      localStorage.removeItem('orgId');
      localStorage.removeItem('orgName');
      localStorage.removeItem('membershipStatus');
      localStorage.removeItem('orgOwnerEmail');
      localStorage.removeItem('subscriptionStatus');
      localStorage.removeItem('trialStart');
      localStorage.removeItem('trialEnd');
      localStorage.removeItem('daysRemaining');
      localStorage.removeItem('aiQuotaRemaining');
      localStorage.removeItem('aiQuotaTotal');
    }
  },

  /**
   * Get current user
   * @returns {Promise<Object>} - Current user data
   */
  async getCurrentUser() {
    return api.get('/auth/me');
  },

  /**
   * Update onboarding step
   * @param {Object} userData - User data
   * @returns {Promise<Object>} - Onboarding progress response
   */
  async updateOnboardingStep(userId, userData) {
    //Token is automatically added by api.js from localStorage
    // Ensure user is authenticated before calling
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please login first.');
    }
    return api.put(`/auth/onboarding/profile/${userId}`, userData);
  },

  /**
   * Update onboarding data
   * @param {Object} userData - User data
   * @returns {Promise<Object>} - Onboarding progress response
   */
  async updateOnboarding(userId, userData) {
    //Token is automatically added by api.js from localStorage
    // Ensure user is authenticated before calling
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please login first.');
    }
    return api.put(`/auth/onboarding/profile/${userId}`, userData);
  },

  /**
   * Update user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Success response
   */
  async updatePassword(currentPassword, newPassword) {
    const encryptedCurrent = await securityService.encrypt(currentPassword);
    const encryptedNew = await securityService.encrypt(newPassword);
    return api.post('/auth/change-password', {
      currentPassword: encryptedCurrent,
      newPassword: encryptedNew,
    });
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} - Success response
   */
  async requestPasswordReset(email) {
    return api.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Success response
   */
  async resetPassword(token, newPassword) {
    const encryptedPassword = await securityService.encrypt(newPassword);
    return api.post('/auth/reset-password', { token, newPassword: encryptedPassword });
  },

  /**
   * Refresh authentication token and get onboarding status
   * @returns {Promise<Object>} - Refresh response with onboarding status
   * @example Response:
   * {
   *   token: "new-jwt-token",
   *   refreshToken: "new-refresh-token",
   *   userId: "123e4567-e89b-12d3-a456-426614174000",
   *   email: "user@example.com",
   *   role: "INVESTOR",
   *   onboardingCompleted: false,
   *   currentStep: 2
   * }
   */
  async refreshToken(token) {
    //const refreshToken = localStorage.getItem('refreshToken');
    if (!token) {
      throw new Error('No Auth token available');
    }
    const response = await api.post('/auth/refresh-token', { token });
    return response;
  },

    /**
   * Login/Sign up with Google
   * @param {Object} googleUserData - Google user data
   * @returns {Promise<Object>} - User data and token
   */
  async loginWithGoogle(googleUserData) {
    const response = await api.post('/auth/login/google', {
      email: googleUserData.email,
      name: googleUserData.name,
      firstName: googleUserData.firstName,
      lastName: googleUserData.lastName,
      googleId: googleUserData.id,
      imageUrl: googleUserData.imageUrl,
      idToken: googleUserData.idToken,
      provider: googleUserData.provider
    });
    
    if (response.token) {
      localStorage.setItem('authToken', response.token);
    }

    if (response.userId) {
      localStorage.setItem('userId', response.userId);
    }

    if (response.email) {
      localStorage.setItem('userEmail', response.email);
    }
    
    if (response.role) {
      localStorage.setItem('userRole', response.role);
    }

    if (response.fullName) {
      localStorage.setItem('fullName', response.fullName);
    }
    if (response.orgAccessRequired !== undefined) {
      localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
    }
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
    }
    if (response.orgName) {
      localStorage.setItem('orgName', response.orgName);
    }
    if (response.membershipStatus) {
      localStorage.setItem('membershipStatus', response.membershipStatus);
    }
    if (response.orgOwnerEmail) {
      localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
    }
    if (response.subscriptionStatus) {
      localStorage.setItem('subscriptionStatus', response.subscriptionStatus);
      localStorage.removeItem('trialSubscriptionSkipped');
    }
    if (response.trialStart) {
      localStorage.setItem('trialStart', response.trialStart);
    }
    if (response.trialEnd) {
      localStorage.setItem('trialEnd', response.trialEnd);
    }
    if (response.daysRemaining !== undefined) {
      localStorage.setItem('daysRemaining', String(response.daysRemaining));
    }
    if (response.aiQuotaRemaining !== undefined) {
      localStorage.setItem('aiQuotaRemaining', String(response.aiQuotaRemaining));
    }
    if (response.aiQuotaTotal !== undefined) {
      localStorage.setItem('aiQuotaTotal', String(response.aiQuotaTotal));
    }
    if (response.isOrgAdmin !== undefined) {
      localStorage.setItem('isOrgAdmin', String(response.isOrgAdmin));
    }
    if (response.isOrgOwner !== undefined) {
      localStorage.setItem('isOrgOwner', String(response.isOrgOwner));
    }
    if (response.subscriptionRequired !== undefined) {
      localStorage.setItem('subscriptionRequired', String(response.subscriptionRequired));
    }
    
    return response;
  },

   /**
    * Login/Sign up with Google using auth-code flow
   * @param {Object} googleUserData - Google user data
   * @returns {Promise<Object>} - User data and token
   */
  async loginWithGoogleAuthCode(googleUserData) {
    const response = await api.post('/auth/login/social/google', {
      code: googleUserData.code,
      redirectUri: googleUserData.redirectUri
    });
    
    if (response.token) {
      localStorage.setItem('authToken', response.token);
    }

    if (response.userId) {
      localStorage.setItem('userId', response.userId);
    }

    if (response.email) {
      localStorage.setItem('userEmail', response.email);
    }
    
    if (response.role) {
      localStorage.setItem('userRole', response.role);
    }

    if (response.fullName) {
      localStorage.setItem('fullName', response.fullName);
    }
    if (response.orgAccessRequired !== undefined) {
      localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
    }
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
    }
    if (response.orgName) {
      localStorage.setItem('orgName', response.orgName);
    }
    if (response.membershipStatus) {
      localStorage.setItem('membershipStatus', response.membershipStatus);
    }
    if (response.orgOwnerEmail) {
      localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
    }
    if (response.subscriptionStatus) {
      localStorage.setItem('subscriptionStatus', response.subscriptionStatus);
      localStorage.removeItem('trialSubscriptionSkipped');
    }
    if (response.trialStart) {
      localStorage.setItem('trialStart', response.trialStart);
    }
    if (response.trialEnd) {
      localStorage.setItem('trialEnd', response.trialEnd);
    }
    if (response.daysRemaining !== undefined) {
      localStorage.setItem('daysRemaining', String(response.daysRemaining));
    }
    if (response.aiQuotaRemaining !== undefined) {
      localStorage.setItem('aiQuotaRemaining', String(response.aiQuotaRemaining));
    }
    if (response.aiQuotaTotal !== undefined) {
      localStorage.setItem('aiQuotaTotal', String(response.aiQuotaTotal));
    }
    if (response.isOrgAdmin !== undefined) {
      localStorage.setItem('isOrgAdmin', String(response.isOrgAdmin));
    }
    if (response.isOrgOwner !== undefined) {
      localStorage.setItem('isOrgOwner', String(response.isOrgOwner));
    }
    if (response.subscriptionRequired !== undefined) {
      localStorage.setItem('subscriptionRequired', String(response.subscriptionRequired));
    }
    
    return response;
  },

  /**
   * Sign up with Google
   * @param {Object} googleUserData - Google user data
   * @returns {Promise<Object>} - User data and token
   */
  async signupWithGoogle(googleUserData) {
    const response = await api.post('/auth/google/signup', {
      email: googleUserData.email,
      name: googleUserData.name,
      firstName: googleUserData.firstName,
      lastName: googleUserData.lastName,
      googleId: googleUserData.id,
      imageUrl: googleUserData.imageUrl,
      idToken: googleUserData.token
    });
    
    if (response.token) {
      localStorage.setItem('authToken', response.token);
    }

    if (response.userId) {
      localStorage.setItem('userId', response.userId);
    }
    
    if (response.role) {
      localStorage.setItem('userRole', response.role);
    }

    if (response.fullName) {
      localStorage.setItem('fullName', response.fullName);
    }
    if (response.orgAccessRequired !== undefined) {
      localStorage.setItem('orgAccessRequired', String(response.orgAccessRequired));
    }
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
    }
    if (response.orgName) {
      localStorage.setItem('orgName', response.orgName);
    }
    if (response.membershipStatus) {
      localStorage.setItem('membershipStatus', response.membershipStatus);
    }
    if (response.orgOwnerEmail) {
      localStorage.setItem('orgOwnerEmail', response.orgOwnerEmail);
    }
    if (response.subscriptionStatus) {
      localStorage.setItem('subscriptionStatus', response.subscriptionStatus);
      localStorage.removeItem('trialSubscriptionSkipped');
    }
    if (response.trialStart) {
      localStorage.setItem('trialStart', response.trialStart);
    }
    if (response.trialEnd) {
      localStorage.setItem('trialEnd', response.trialEnd);
    }
    if (response.daysRemaining !== undefined) {
      localStorage.setItem('daysRemaining', String(response.daysRemaining));
    }
    if (response.aiQuotaRemaining !== undefined) {
      localStorage.setItem('aiQuotaRemaining', String(response.aiQuotaRemaining));
    }
    if (response.aiQuotaTotal !== undefined) {
      localStorage.setItem('aiQuotaTotal', String(response.aiQuotaTotal));
    }
    if (response.isOrgAdmin !== undefined) {
      localStorage.setItem('isOrgAdmin', String(response.isOrgAdmin));
    }
    if (response.isOrgOwner !== undefined) {
      localStorage.setItem('isOrgOwner', String(response.isOrgOwner));
    }
    if (response.subscriptionRequired !== undefined) {
      localStorage.setItem('subscriptionRequired', String(response.subscriptionRequired));
    }
    
    return response;
  }
};

export default authService;
